import { useState } from 'react'
import { Sparkles } from 'lucide-react'
import { api, tokens, ApiError } from '../api'
import type { Tokens } from '../api'
import { Button, Field, Input, ErrorText, Note } from '../components/ui'

type AuthResponse = { tokens: Tokens; note?: string }

export function Login({ onSignedIn, ownerSetup = false }: {
  onSignedIn: () => void
  ownerSetup?: boolean
}) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [setupToken, setSetupToken] = useState('')

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true); setError('')
    try {
      const path = ownerSetup ? '/api/auth/register' : '/api/auth/login'
      const res = await api.post<AuthResponse>(path, { email, password, setup_token: setupToken })
      tokens.set(res.tokens)
      onSignedIn()
    } catch (err) {
      // The server deliberately gives the same answer for a wrong password and
      // an unknown address, so there is nothing more specific to say here.
      setError(err instanceof ApiError ? err.message : 'Something went wrong')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="grid min-h-screen place-items-center px-5">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <span className="grid size-11 place-items-center rounded-xl bg-brand text-brand-ink shadow-[0_4px_20px_oklch(0.78_0.155_72/0.35)]">
            <Sparkles className="size-5" />
          </span>
          <div>
            <h1 className="text-xl">ReelWorks</h1>
            <p className="mt-1 text-sm text-ink-faint">
              UGC videos, from one photo and a pain point.
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="panel space-y-4 p-6">
          <Field label="Email" htmlFor="email">
            <Input
              id="email" type="email" value={email} required autoFocus autoComplete="email"
              placeholder="you@company.com"
              onChange={e => setEmail(e.target.value)}
            />
          </Field>

          <Field
            label="Password"
            htmlFor="password"
            help={ownerSetup
              ? 'At least 12 characters. A few plain words beat a short one with symbols.'
              : undefined}
          >
            <Input
              id="password" type="password" value={password} required
              autoComplete={ownerSetup ? 'new-password' : 'current-password'}
              onChange={e => setPassword(e.target.value)}
            />
          </Field>

          {ownerSetup && <Field label="Owner setup token"><Input type="password" value={setupToken} onChange={event => setSetupToken(event.target.value)} required autoComplete="off" /></Field>}
          {error && <ErrorText error={error} />}

          <Button
            type="submit" variant="primary" className="w-full"
            disabled={busy || !email || !password}
          >
            {busy ? 'Working…' : ownerSetup ? 'Create owner account' : 'Sign in'}
          </Button>

          {!ownerSetup && <a className="block text-center text-sm underline" href="/forgot-password">Forgot password?</a>}

          {ownerSetup && (
            <Note>Use the expiring setup token supplied by your installation administrator to create the first owner.</Note>
          )}
        </form>
      </div>
    </div>
  )
}
